import { useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingUp, Users as UsersIcon, Package, Download, FileText, ImageIcon, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

/**
 * HistoricoSemanalChart — Evolução semana-a-semana dos top-N entregadores no
 * painel de Logística. Backend fornece `historico_semanal = { weeks, series }`.
 *
 * Métricas disponíveis:
 *  - "Volume" (total): número de entregas concluídas por semana
 *  - "Tempo médio (min)": tempo médio de confirmação por semana
 *
 * Export (fev/2026): supervisor pode baixar o gráfico como PNG (SVG do recharts
 * serializado → canvas 2x → blob) ou CSV (weeks × entregadores, com BOM UTF-8).
 */

// Paleta harmônica com o restante do app (laranja/emerald/blue/purple/teal/amber)
const COLORS = ["#EA580C", "#10B981", "#3B82F6", "#8B5CF6", "#14B8A6", "#F59E0B"];

const brWeekLabel = (iso) => {
  if (!iso) return "";
  const [_y, m, d] = iso.split("-");
  return `${d}/${m}`;
};

/**
 * Serializa o <svg> do recharts em PNG num canvas 2x (retina) com fundo branco.
 * Não usa deps externas — DOM API + canvas nativo. Fallback quando o SVG não
 * é encontrado (chart ainda não renderizou): reporta erro via toast.
 */
async function svgToPngBlob(svgEl, scale = 2) {
  if (!svgEl) throw new Error("SVG não encontrado no DOM");
  const rect = svgEl.getBoundingClientRect();
  const width = Math.ceil(rect.width);
  const height = Math.ceil(rect.height);
  // Clona pra não mutar o SVG live; injeta width/height explícitos.
  const clone = svgEl.cloneNode(true);
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const source = new XMLSerializer().serializeToString(clone);
  // btoa não aceita UTF-8 direto — encoda como URI e depois decoda em Latin1
  const dataUrl = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(source)));
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob); else reject(new Error("Falha ao gerar Blob PNG"));
      }, "image/png", 0.95);
    };
    img.onerror = (e) => reject(new Error(`Falha ao carregar SVG como imagem: ${e?.message || e}`));
    img.src = dataUrl;
  });
}

export function HistoricoSemanalChart({ historico }) {
  const [metric, setMetric] = useState("total"); // total | tempo_medio_min
  const [exporting, setExporting] = useState(null); // 'png' | 'csv' | null
  const chartRef = useRef(null);

  // Fallbacks estáveis via useMemo — evita gerar novo array vazio a cada render
  // e disparar re-runs desnecessários no useMemo do chartData (fev/2026).
  const weeks = useMemo(() => historico?.weeks || [], [historico]);
  const series = useMemo(() => historico?.series || [], [historico]);

  // Formato do recharts: uma linha por semana, uma coluna por entregador
  const chartData = useMemo(() => weeks.map((wk) => {
    const row = { week: wk, label: brWeekLabel(wk) };
    series.forEach((s) => {
      const point = s.data.find((d) => d.week === wk) || { total: 0, tempo_medio_min: 0 };
      row[s.nome] = point[metric] || 0;
    });
    return row;
  }), [weeks, series, metric]);

  const isEmpty = series.length === 0 || weeks.length === 0;
  const metricLabel = metric === "total" ? "volume" : "tempo_medio";
  const todayStr = new Date().toISOString().slice(0, 10);

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const exportPNG = async () => {
    if (isEmpty) return toast.error("Sem dados suficientes para exportar");
    setExporting("png");
    try {
      const svg = chartRef.current?.querySelector("svg");
      const blob = await svgToPngBlob(svg, 2);
      triggerDownload(blob, `historico_semanal_${metricLabel}_${todayStr}.png`);
      toast.success("PNG baixado — pronto pra reunião");
    } catch (e) {
      console.error("Falha ao exportar PNG do histórico", e);
      toast.error(`Erro ao gerar PNG: ${e.message}`);
    } finally { setExporting(null); }
  };

  const exportCSV = () => {
    if (isEmpty) return toast.error("Sem dados suficientes para exportar");
    setExporting("csv");
    try {
      const metricUnit = metric === "total" ? "entregas" : "min";
      const header = ["semana_inicio", "semana_label", ...series.map((s) => `${s.nome} (${metricUnit})`)];
      const lines = [header.map((h) => `"${h.replace(/"/g, '""')}"`).join(",")];
      chartData.forEach((row) => {
        const cells = [row.week, row.label, ...series.map((s) => row[s.nome] ?? 0)];
        lines.push(cells.map((c) => (typeof c === "string" ? `"${c.replace(/"/g, '""')}"` : c)).join(","));
      });
      const csv = "\ufeff" + lines.join("\n"); // BOM UTF-8 pra Excel abrir com acentos
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      triggerDownload(blob, `historico_semanal_${metricLabel}_${todayStr}.csv`);
      toast.success(`CSV baixado (${chartData.length} semanas × ${series.length} entregadores)`);
    } catch (e) {
      console.error("Falha ao exportar CSV do histórico", e);
      toast.error("Erro ao gerar CSV");
    } finally { setExporting(null); }
  };

  return (
    <Card data-testid="historico-semanal-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="w-5 h-5 text-orange-600" />
          Histórico semanal por entregador
          {series.length > 0 && (
            <Badge variant="outline" className="text-xs">
              <UsersIcon className="w-3 h-3 mr-1" />Top {series.length}
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Toggle métrica */}
          <div className="flex rounded-md border border-slate-200 overflow-hidden shadow-sm" role="tablist" data-testid="historico-metric-toggle">
            <button
              type="button"
              onClick={() => setMetric("total")}
              className={`px-3 h-8 text-xs font-semibold inline-flex items-center gap-1.5 transition ${metric === "total" ? "bg-orange-600 text-white" : "bg-white hover:bg-slate-50 text-slate-700"}`}
              data-testid="historico-metric-total"
            >
              <Package className="w-3.5 h-3.5" />Volume
            </button>
            <button
              type="button"
              onClick={() => setMetric("tempo_medio_min")}
              className={`px-3 h-8 text-xs font-semibold inline-flex items-center gap-1.5 transition ${metric === "tempo_medio_min" ? "bg-orange-600 text-white" : "bg-white hover:bg-slate-50 text-slate-700"}`}
              data-testid="historico-metric-tempo"
            >
              <TrendingUp className="w-3.5 h-3.5" />Tempo médio
            </button>
          </div>
          {/* Export buttons — só mostra quando há dados */}
          {!isEmpty && (
            <div className="flex items-center gap-1" data-testid="historico-export-group">
              <Button
                size="sm"
                variant="outline"
                onClick={exportPNG}
                disabled={!!exporting}
                className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                title="Baixar como imagem PNG"
                data-testid="btn-export-png"
              >
                {exporting === "png"
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /></>
                  : <><ImageIcon className="w-3.5 h-3.5 mr-1" />PNG</>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={exportCSV}
                disabled={!!exporting}
                className="h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
                title="Baixar dados brutos como CSV"
                data-testid="btn-export-csv"
              >
                {exporting === "csv"
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /></>
                  : <><FileText className="w-3.5 h-3.5 mr-1" />CSV</>}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <div className="p-8 text-center text-muted-foreground bg-slate-50 rounded-lg" data-testid="historico-empty">
            <TrendingUp className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            Ainda não há entregas concluídas suficientes para montar a evolução semanal.
            <div className="text-xs mt-1">O gráfico aparece assim que os entregadores começarem a fechar entregas ao longo das semanas.</div>
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground mb-2">
              ⚡ Últimas <b>{weeks.length}</b> semanas · segunda-feira a domingo · top <b>{series.length}</b> entregadores por volume total
            </div>
            <div style={{ height: 320 }} className="min-w-0" ref={chartRef}>
              <ResponsiveContainer width="99%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" fontSize={11} tick={{ fill: "#64748B" }} />
                  <YAxis fontSize={11} tick={{ fill: "#64748B" }}
                    label={{ value: metric === "total" ? "Entregas" : "Minutos", angle: -90, position: "insideLeft", fontSize: 11, fill: "#64748B" }} />
                  <Tooltip
                    contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}
                    formatter={(value, name) => [metric === "total" ? `${value} entregas` : `${value} min`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="line" />
                  {series.map((s, i) => (
                    <Line
                      key={s.entregador_id}
                      type="monotone"
                      dataKey={s.nome}
                      stroke={COLORS[i % COLORS.length]}
                      strokeWidth={2.5}
                      dot={{ r: 3, strokeWidth: 2 }}
                      activeDot={{ r: 6 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs" data-testid="historico-totals">
              {series.map((s, i) => (
                <div key={s.entregador_id} className="flex items-center gap-2 p-1.5 bg-slate-50 rounded">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="truncate flex-1"><b>{s.nome}</b></span>
                  <span className="text-slate-600 whitespace-nowrap">{s.total_periodo} no total</span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
